import React, { useState, useMemo, useCallback } from "react";
import { UilAngleDoubleRight, UilCalendarAlt } from "@iconscout/react-unicons";

import {
  captionLabelClassGenerator,
  imageClassGenerator,
  headingClassGenerator,
  paddingClassGenerator,
  sectionClassGenerator,
  buttonClassGenerator,
  getCardHeightClass,
  truncateText,
} from "../../helper/helper";
import urlConfig from "../../helper/urlConfig";

/**
 * NewsAndEventsItem Component
 * 
 * A responsive card component for displaying news and events.
 * Following Single Responsibility Principle - focused on rendering a single item.
 * 
 * @param {Object} props - Component props
 * @param {Object} props.item - The news/event data
 * @param {Object} props.currentPageConfig - Page configuration from Redux
 * @param {string} props.type - Current selected type (news/events)
 */
const NewsAndEventsItem = ({ item, currentPageConfig, type }) => {
  // Destructure item properties with defaults
  const {
    id,
    images = [],
    tags = [],
    title = "",
    publish_date,
    content = "",
    url = "",
  } = item || {};

  // Hover state for interactive elements
  const [isHovered, setIsHovered] = useState(false);

  // Memoized class generation for performance
  const classes = useMemo(() => {
    const columns = currentPageConfig?.columns;
    return {
      label: captionLabelClassGenerator(columns),
      image: imageClassGenerator(columns),
      heading: headingClassGenerator(columns),
      padding: paddingClassGenerator(columns),
      section: sectionClassGenerator(columns),
      button: buttonClassGenerator(columns),
      cardHeight: getCardHeightClass(type),
    };
  }, [currentPageConfig, type]);

  // Memoized values
  const customTags = useMemo(() => 
    tags?.length > 0 ? tags.map(x => x.name).join(", ") : "",
    [tags]
  );

  const imageUrl = useMemo(() => 
    images?.length > 0 ? `${urlConfig.baseUrl}${images[0].upload_path}` : null,
    [images]
  );

  const isLeftLayout = useMemo(() => 
    currentPageConfig?.post_summary_info_type === "left",
    [currentPageConfig]
  );

  const isBelowImageLayout = useMemo(() => 
    currentPageConfig?.post_summary_info_type === "below-image",
    [currentPageConfig]
  );

  // Content truncation
  const truncatedContent = useMemo(() => 
    truncateText(content?.replace(/<[^>]*>/g, ''), 150),
    [content]
  );

  const truncatedTitle = useMemo(() => 
    truncateText(title, 80),
    [title]
  );

  // Format date for display
  const formattedDate = useMemo(() => {
    if (!publish_date) return null;
    try {
      return new Date(publish_date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return publish_date;
    }
  }, [publish_date]);

  // Event handlers
  const handleReadMore = useCallback((e) => {
    e.preventDefault();
    if (url) {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  }, [url]);

  const handleMouseEnter = useCallback(() => setIsHovered(true), []);
  const handleMouseLeave = useCallback(() => setIsHovered(false), []);

  return (
    <article
      className={`
        w-full flex flex-col rounded-xl overflow-hidden
        bg-white shadow-md hover:shadow-xl
        transition-all duration-300 ease-in-out
        transform hover:-translate-y-1
        ${classes.cardHeight}
      `}
    >
      {/* Left sidebar for tags (conditional) */}
      {isLeftLayout && (
        <aside className="hidden md:flex md:w-full px-3 py-2 bg-gray-50 border-b">
          <span className={`${classes.label} text-indigo-600`}>
            {customTags}
          </span>
        </aside>
      )}

      {/* Image Section */}
      <div 
        className={`relative ${classes.image} overflow-hidden`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {imageUrl ? (
          <img
            crossOrigin="anonymous"
            src={imageUrl}
            alt={title}
            loading="lazy"
            className={`
              h-full w-full object-cover
              transition-all duration-500 ease-in-out
              ${isHovered ? "scale-110 blur-sm" : "scale-100"}
            `}
          />
        ) : (
          <div className="h-full w-full bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center">
            <span className="text-indigo-400 text-sm font-medium">
              {type === 'events' ? '📅 Event' : '📰 News'}
            </span>
          </div>
        )}

        {/* Type Badge */}
        <div className="absolute top-2 sm:top-3 left-2 sm:left-3">
          <span className={`
            px-2 sm:px-3 py-1 rounded-full text-xs font-semibold uppercase
            ${type === 'events' 
              ? 'bg-purple-500 text-white' 
              : 'bg-indigo-500 text-white'
            }
          `}>
            {type}
          </span>
        </div>

        {/* Hover Overlay with Button */}
        <div 
          className={`
            absolute inset-0 bg-black/40 flex items-center justify-center
            transition-opacity duration-300
            ${isHovered ? "opacity-100" : "opacity-0"}
          `}
        >
          <button
            type="button"
            className={`
              ${classes.button}
              transform transition-all duration-300
              ${isHovered ? "scale-100 translate-y-0" : "scale-95 translate-y-4"}
            `}
            onClick={handleReadMore}
            aria-label={`Read more about ${title}`}
          >
            <span>Read More</span>
            <UilAngleDoubleRight size={18} />
          </button>
        </div>
      </div>

      {/* Content Section */}
      <div className={`flex-1 flex flex-col ${classes.padding} py-3 sm:py-4`}>
        {/* Date & Tags */}
        <div className="flex flex-wrap items-center gap-2 mb-2">
          {formattedDate && (
            <div className="flex items-center gap-1 text-gray-500 text-xs sm:text-sm">
              <UilCalendarAlt size={14} />
              <span>{formattedDate}</span>
            </div>
          )}
          {isBelowImageLayout && customTags && (
            <span className={`${classes.label} text-indigo-600 text-xs`}>
              {customTags}
            </span>
          )}
        </div>

        {/* Title */}
        <h3 className={`${classes.heading} text-gray-800 hover:text-indigo-600 transition-colors`}>
          {truncatedTitle}
        </h3>

        {/* Content Preview */}
        <div className={`${classes.section} flex-1`}>
          <p className="leading-relaxed">
            {truncatedContent}
          </p>
        </div>

        {/* Mobile Read More Button */}
        <div className="mt-auto pt-3 sm:hidden">
          <button
            type="button"
            onClick={handleReadMore}
            className={`
              w-full py-2 px-4 text-white text-sm font-medium rounded-lg
              transition-colors duration-200 flex items-center justify-center gap-2
              ${type === 'events' 
                ? 'bg-purple-500 hover:bg-purple-600' 
                : 'bg-indigo-500 hover:bg-indigo-600'
              }
            `}
          >
            <span>Read More</span>
            <UilAngleDoubleRight size={16} />
          </button>
        </div>
      </div>
    </article>
  );
};

export default NewsAndEventsItem;
